import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CombinationCompareGrid from './CombinationCompareGrid2';
import CombinationWorkspace from './CombinationWorkspace4';
import * as api from './api';
import { friendlyMessage } from '../../../api/apiClient';
import { LoadingState, ErrorState, useToast } from '../../../shared/components/status';
import { GREEN, CHARCOAL, MUTED, pageTitle, pageTitleAccent, pageDescription, PAGE_PX } from '../../../theme/tokens2';

// ─── SpacePlanning ──────────────────────────────────────────────────────────
// Top-level container for the layout-options tool. Loads all layouts
// ("combinations") and the shared furniture catalog, switches between the
// compare view and the single-layout workspace, and saves every change
// (selection, rank, notes). Each change shows on screen straight away; if
// the save fails, the customer sees a message and the screen is put back.
//
// Also works out `pxPerMm`: one mm-to-px scale used by LayoutThumb so room
// thumbnails are sized by their real dimensions. Every layout in a project
// shares the same apartment outline, so it only needs working out once.

const MAX_THUMB_TARGET_PX = 270; // px -- the LONGEST side of the apartment maps to roughly this

export default function SpacePlanning() {
  const toast = useToast();

  const [combinations,    setCombinations]    = useState(null);
  const [furniturePieces, setFurniturePieces] = useState(null);
  const [status,          setStatus]          = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError,       setLoadError]       = useState('');
  const [comparing,       setComparing]       = useState(true);
  const [activeId,        setActiveId]        = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setStatus('loading');
    try {
      const [combosData, piecesData] = await Promise.all([api.fetchCombinations(), api.fetchFurniturePieces()]);
      const sorted = [...(combosData || [])].sort((a, b) => a.rankOrder - b.rankOrder);
      setCombinations(sorted);
      setFurniturePieces(piecesData || []);
      setActiveId((prev) => prev ?? sorted[0]?.id ?? null);
      setStatus('ready');
    } catch (err) {
      if (quiet) return; // a quiet reload after a failed save: keep what's on screen
      setLoadError(friendlyMessage(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeCombination = useMemo(
    () => combinations?.find((c) => c.id === activeId) || null,
    [combinations, activeId]
  );

  // Every layout shares the same apartment outline, so any one gives the answer.
  const pxPerMm = useMemo(() => {
    if (!combinations || combinations.length === 0) return null;
    const anyCombination = combinations[0];
    const apartmentLongestSideMm = Math.max(anyCombination.totalWidthMm, anyCombination.totalHeightMm);
    return MAX_THUMB_TARGET_PX / apartmentLongestSideMm;
  }, [combinations]);

  function handleTry(combinationId) {
    setActiveId(combinationId);
    setComparing(false);
  }

  function handleBackToCompare() {
    setComparing(true);
  }

  function handleMoveRank(combinationId, direction) {
    setCombinations((prev) => {
      const sorted = [...prev].sort((a, b) => a.rankOrder - b.rankOrder);
      const index = sorted.findIndex((c) => c.id === combinationId);
      const neighbourIndex = index + direction;
      if (neighbourIndex < 0 || neighbourIndex >= sorted.length) return prev;

      const next = sorted.map((c) => ({ ...c }));
      const targetRank = next[index].rankOrder;
      next[index].rankOrder = next[neighbourIndex].rankOrder;
      next[neighbourIndex].rankOrder = targetRank;
      return next.sort((a, b) => a.rankOrder - b.rankOrder);
    });
    api.updateRank(combinationId, direction).catch(() => {
      toast.error("We couldn't save your new order. Please try again.");
      load({ quiet: true }); // put the saved order back on screen
    });
  }

  function handleSelectLayout(combinationId, roomLabel, roomUseName, layoutOptionId) {
    setCombinations((prev) =>
      prev.map((combo) =>
        combo.id !== combinationId
          ? combo
          : {
              ...combo,
              rooms: combo.rooms.map((room) =>
                room.label === roomLabel
                  ? {
                      ...room,
                      // A falsy layoutOptionId means Empty was selected.
                      selection: layoutOptionId
                        ? { selectedRoomUseName: roomUseName, selectedLayoutOptionId: layoutOptionId }
                        : { selectedRoomUseName: null, selectedLayoutOptionId: null },
                    }
                  : room
              ),
            }
      )
    );
    api.updateSelection(combinationId, roomLabel, roomUseName, layoutOptionId).catch(() => {
      toast.error(`We couldn't save your choice for ${roomLabel}. Please try again.`);
      load({ quiet: true });
    });
  }

  function handleSaveNotes(combinationId, text) {
    const previous = combinations.find((c) => c.id === combinationId)?.notes ?? '';
    setCombinations((prev) =>
      prev.map((c) => (c.id === combinationId ? { ...c, notes: text } : c))
    );
    api.updateNotes(combinationId, text).catch(() => {
      // The typed text stays in the notes box. Marking the saved notes as the
      // old value means clicking into the box and out again tries once more.
      setCombinations((prev) =>
        prev.map((c) => (c.id === combinationId ? { ...c, notes: previous } : c))
      );
      toast.error("We couldn't save your notes. Click into the notes and out again to retry.");
    });
  }

  if (status === 'loading') {
    return <LoadingState label="Loading your layout options…" size="page" />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        size="page"
        title="We couldn't load your layout options"
        message={loadError}
        onRetry={() => load()}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 1160, px: PAGE_PX, py: 3, minHeight: '100%' }}>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2.25, gap: 2, flexWrap: 'wrap' }}>
        {comparing ? (
          <Box>
            <Typography component="h1" sx={pageTitle}>
              Compare your{' '}
              <Box component="span" sx={pageTitleAccent}>
                layouts.
              </Box>
            </Typography>
            <Typography sx={pageDescription}>
              {combinations.length === 0
                ? 'Our team is preparing layout ideas for your home. Check back soon.'
                : <>Our team has put together {combinations.length} early layout idea{combinations.length !== 1 ? 's' : ''} for
                  your home. Explore how every room could be used, bump your favourite to the top, and bring them
                  along as a starting point when you meet your interior designer.</>}
            </Typography>
          </Box>
        ) : (
          <Typography component="nav" aria-label="Breadcrumb" sx={{ fontSize: '13.5px', color: MUTED, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box
              component="button"
              type="button"
              onClick={handleBackToCompare}
              sx={{
                color: GREEN, fontWeight: 500, cursor: 'pointer',
                background: 'none', border: 'none', p: 0, font: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: '0.9rem' }} />
              Layout options
            </Box>
            <Box component="span" aria-hidden sx={{ color: MUTED }}>/</Box>
            <Box component="span" sx={{ color: CHARCOAL, fontWeight: 600 }}>
              {activeCombination?.name}
            </Box>
          </Typography>
        )}
      </Box>

      {comparing ? (
        <CombinationCompareGrid
          combinations={combinations}
          furniturePieces={furniturePieces}
          selectedId={activeId}
          onSelect={setActiveId}
          onTry={handleTry}
          onMoveUp={(id) => handleMoveRank(id, -1)}
          onMoveDown={(id) => handleMoveRank(id, 1)}
        />
      ) : activeCombination ? (
        <CombinationWorkspace
          key={activeCombination.id}
          combination={activeCombination}
          furniturePieces={furniturePieces}
          pxPerMm={pxPerMm}
          onSelectLayout={(roomLabel, roomUseName, layoutOptionId) =>
            handleSelectLayout(activeCombination.id, roomLabel, roomUseName, layoutOptionId)
          }
          onSaveNotes={(text) => handleSaveNotes(activeCombination.id, text)}
        />
      ) : null}

    </Box>
  );
}
